<template>
    <!-- the MVP of the match (best efficiency of the winning team), or the best player of the other team -->
    <router-link v-if="player && player.id" :to="{ path: `/Player/${player.id}` }" class="mvp" :class="{ 'mvp--secondary': secondary }">
        <span class="mvp__photo">
            <img :src="photo" alt="" @error="$event.target.src = defaultPlayerImg">
        </span>
        <span class="mvp__body">
            <span class="mvp__badge"><v-icon small color="#232323">{{ secondary ? 'mdi-star' : 'mdi-trophy' }}</v-icon>{{ badge }}</span>
            <span class="mvp__name">{{ fullName }}</span>
            <span v-if="team" class="mvp__team">
                <span class="cl-logo mvp__team-logo"><img v-if="team.logo" :src="`${assetsUrl}/${team.logo}`" alt=""></span>
                {{ team.name }}<template v-if="player.jersey"> · #{{ player.jersey }}</template>
            </span>
            <span class="mvp__stats">
                <span v-for="stat in stats" :key="stat.value" class="mvp__stat" :class="{ 'mvp__stat--lead': stat.value === 'pir' }">
                    <span class="mvp__stat-value">{{ line[stat.value] || 0 }}</span>
                    <span class="mvp__stat-label">{{ stat.text }}</span>
                </span>
            </span>
        </span>
    </router-link>
</template>

<script>
    export default {
        props: {
            player: {
                type: Object,
                default: null
            },
            team: {
                type: Object,
                default: null
            },
            live: {
                type: Boolean,
                default: false
            },
            // the best player of the other team
            secondary: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                stats: [
                    { text: 'EFF', value: 'pir' },
                    { text: 'PTS', value: 'points' },
                    { text: 'REB', value: 'rebounds' },
                    { text: 'AST', value: 'assists' },
                    { text: 'STL', value: 'steals' },
                    { text: 'BLK', value: 'blockedShots' }
                ],
                assetsUrl: 'https://storage.googleapis.com',
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            badge() {
                if (this.secondary) return 'Κορυφαίος ομάδας'
                return this.live ? 'Κορυφαίος μέχρι τώρα' : 'MVP αγώνα'
            },
            line() {
                return (this.player.stats && this.player.stats[0]) || {}
            },
            photo() {
                return this.player.image ? `${this.assetsUrl}/${this.player.image}` : this.defaultPlayerImg
            },
            fullName() {
                return `${this.player.name || ''} ${this.player.surname || ''}`.trim()
            }
        }
    }
</script>

<style scoped>
.mvp {
    position: relative;
    display: flex;
    align-items: stretch;
    border-radius: 14px;
    background: #232323;
    color: #fff !important;
    text-decoration: none;
    box-shadow: 0 10px 30px rgba(35, 35, 35, 0.16);
    overflow: hidden;
}

.mvp::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 12% 50%, rgba(255, 196, 0, 0.18), transparent 55%);
    pointer-events: none;
}

.mvp__photo {
    position: relative;
    flex: 0 0 180px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    background: linear-gradient(160deg, #ffd54f 0%, #ff9800 100%);
}

.mvp__photo img {
    display: block;
    width: 100%;
    height: 100%;
    max-height: 240px;
    object-fit: cover;
    object-position: top center;
}

.mvp__body {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 8px;
    min-width: 0;
    padding: 22px 26px;
}

.mvp__badge {
    display: inline-flex;
    align-self: flex-start;
    align-items: center;
    gap: 6px;
    padding: 3px 12px;
    border-radius: 999px;
    background: linear-gradient(135deg, #ffd54f 0%, #ffb300 100%);
    color: #232323;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.75rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.mvp__name {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2.1rem;
    line-height: 1;
    letter-spacing: 0.02em;
    text-transform: uppercase;
}

.mvp:hover .mvp__name {
    color: #ffd54f;
}

.mvp__team {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: rgba(255, 255, 255, 0.65);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 600;
}

.mvp__team-logo {
    width: 24px;
    height: 24px;
    border-radius: 5px;
}

.mvp__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 6px;
}

.mvp__stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 62px;
    padding: 8px 10px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.08);
}

.mvp__stat--lead {
    background: linear-gradient(135deg, #ffd54f 0%, #ffb300 100%);
    border-color: transparent;
    color: #232323;
}

.mvp__stat-value {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    line-height: 1;
}

.mvp__stat-label {
    margin-top: 2px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.68rem;
    font-weight: 800;
    letter-spacing: 0.08em;
    color: #03e5b7;
}

.mvp__stat--lead .mvp__stat-label {
    color: #232323;
}

/* the other team: quieter than the MVP, no gold, smaller */
.mvp--secondary {
    background: #2b2b2b;
    box-shadow: 0 6px 18px rgba(35, 35, 35, 0.1);
}

.mvp--secondary::before {
    display: none;
}

.mvp--secondary .mvp__photo {
    flex-basis: 120px;
    background: #3a3a3a;
}

.mvp--secondary .mvp__body {
    gap: 6px;
    padding: 16px 18px;
}

.mvp--secondary .mvp__badge {
    padding: 2px 10px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.75);
    font-size: 0.68rem;
}

.mvp--secondary .mvp__badge .v-icon {
    color: #03e5b7 !important;
}

.mvp--secondary .mvp__name {
    font-size: 1.5rem;
}

.mvp--secondary .mvp__stat {
    min-width: 46px;
    padding: 5px 6px;
}

.mvp--secondary .mvp__stat-value {
    font-size: 1.25rem;
}

.mvp--secondary .mvp__stat--lead {
    background: rgba(3, 229, 183, 0.12);
    border-color: rgba(3, 229, 183, 0.3);
    color: #fff;
}

.mvp--secondary .mvp__stat--lead .mvp__stat-label {
    color: #03e5b7;
}

.mvp--secondary:hover .mvp__name {
    color: #03e5b7;
}

@media (max-width: 600px) {
    .mvp {
        flex-direction: column;
    }

    .mvp__photo {
        flex-basis: auto;
        height: 200px;
    }

    .mvp__photo img {
        max-height: 200px;
        width: auto;
    }

    .mvp__body {
        padding: 16px;
    }

    .mvp__name {
        font-size: 1.9rem;
    }

    .mvp__stats {
        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: 6px;
    }

    .mvp__stat {
        min-width: 0;
        padding: 6px 2px;
    }

    .mvp__stat-value {
        font-size: 1.35rem;
    }

    .mvp__stat-label {
        font-size: 0.6rem;
    }
}

/* on a phone the other team stays a slim row */
@media (max-width: 600px) {
    .mvp--secondary {
        flex-direction: row;
    }

    .mvp--secondary .mvp__photo {
        flex: 0 0 84px;
        height: auto;
    }

    .mvp--secondary .mvp__photo img {
        width: 100%;
        max-height: none;
    }

    .mvp--secondary .mvp__body {
        padding: 12px;
    }

    .mvp--secondary .mvp__name {
        font-size: 1.25rem;
    }

    .mvp--secondary .mvp__stats {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .mvp--secondary .mvp__stat-value {
        font-size: 1.1rem;
    }
}
</style>
